import { useEffect, useState, useRef } from 'react';
import { usePlatformStore } from '@/stores/platformStore';
import { useConversationStore } from '@/stores/conversationStore';
import { useAnalyzeConversation } from '@/hooks/useAnalysis';
import { useGenerateDraft } from '@/hooks/useDraft';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import toast from '@/lib/toast';
import SentimentDisplay from './SentimentDisplay';
import QuestionList from './QuestionList';
import DraftEditor from './DraftEditor';
import ChatInterface from './ChatInterface';
import CustomerNotes from './CustomerNotes';
import KeyboardShortcutsHelp, { useKeyboardShortcutsHelp } from './KeyboardShortcutsHelp';
import { KeyboardHint } from './ui/KeyboardHint';
import type { Conversation, Sentiment, Question, Draft, PluginSession } from '@/lib/types';

interface SidebarProps {
  conversation: Conversation | null;
  session: PluginSession | null;
  isUserActive?: boolean; // Whether user has active seat
}

export default function Sidebar({ conversation, session, isUserActive = true }: SidebarProps) {
  const { adapter } = usePlatformStore();
  const {
    chatHistory,
    crewflowConversationId,
    setCrewflowConversationId,
    setDraft: setDraftInStore,
    reset: resetStore
  } = useConversationStore();
  const [sentiment, setSentiment] = useState<Sentiment | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<Draft | null>(null);
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);

  // Debug: Count renders
  const renderCount = useRef(0);
  renderCount.current++;

  console.log(`[Sidebar] Render #${renderCount.current}`, {
    conversationId: conversation?.id,
    messageCount: conversation?.messages?.length,
  });

  // Detect render loop
  if (renderCount.current > 10) {
    console.error('[Sidebar] RENDER LOOP DETECTED! Over 10 renders. Something is wrong!');
  }

  const analyzeMutation = useAnalyzeConversation();
  const draftMutation = useGenerateDraft();

  // Keyboard shortcuts help modal
  const { isOpen: isHelpOpen, openHelp, closeHelp } = useKeyboardShortcutsHelp();

  const allQuestionsAnswered =
    questions.length === 0 ||
    questions.filter((q) => q.required).every((q) => answers[q.id]);

  // Auto-analyze when conversation changes (only if user is active)
  useEffect(() => {
    if (conversation && conversation.messages && conversation.messages.length > 0 && isUserActive) {
      // Reset local state for new conversation
      setSentiment(null);
      setQuestions([]);
      setAnswers({});
      setDraft(null);

      // Reset store state (including chat history)
      resetStore();

      // Start analysis
      handleAnalyze();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, isUserActive]); // Re-run when conversation ID or user status changes

  const handleAnalyze = async () => {
    if (!conversation || !isUserActive) return;

    analyzeMutation.mutate(conversation, {
      onSuccess: (data) => {
        setSentiment(data.sentiment);
        setQuestions(data.questions);
        // Store the Crewflow conversation ID for chat endpoint
        if (data.crewflow_conversation_id) {
          setCrewflowConversationId(data.crewflow_conversation_id);
        }
      },
    });
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleGenerateDraft = () => {
    if (!conversation || !isUserActive) return;

    // Check if all required questions are answered
    const unansweredRequired = questions
      .filter((q) => q.required)
      .filter((q) => !answers[q.id]);

    if (unansweredRequired.length > 0) {
      toast.warning('Please answer all required questions', {
        description: 'All required questions must be answered before generating a draft.',
      });
      return;
    }

    draftMutation.mutate(
      {
        conversation_id: conversation.id,
        answers,
      },
      {
        onSuccess: (data) => {
          // Merge the response - backend returns articles separately
          const draftWithArticles = {
            ...data.draft,
            articles: data.articles_used || [],
            confidence: data.confidence || data.draft.confidence,
          };
          setDraft(draftWithArticles);
          setDraftInStore(draftWithArticles); // Store for feedback tracking
        },
      }
    );
  };

  const handleImportFromFront = async () => {
    if (!adapter?.fetchDraft) {
      toast.error('Import not supported', {
        description: 'This platform does not support importing drafts.',
      });
      return;
    }

    try {
      const draftText = await adapter.fetchDraft();

      if (!draftText) {
        toast.warning('No draft found', {
          description: 'There is no draft in Front to import.',
        });
        return;
      }

      // Create a draft object from the imported text
      // Mark it as imported (no AI-generated metadata)
      const importedDraft: Draft = {
        id: `imported_${Date.now()}`, // Temporary ID for local use
        text: draftText,
        confidence: 1.0, // User-written, so 100% confidence
        articles: [],
        created_at: new Date(),
        tone: undefined,
      };

      setDraft(importedDraft);
      // Don't store in conversationStore - this isn't AI-generated
      // so we don't want feedback tracking on it

      toast.success('Draft imported from Front', {
        description: 'You can now edit or regenerate with AI.',
      });
    } catch (error) {
      console.error('Failed to import draft from Front:', error);
      toast.error('Failed to import draft', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleInsertDraft = async (text: string) => {
    if (!adapter) {
      console.error('Platform adapter not initialized');
      toast.error('Platform adapter not initialized');
      return;
    }

    try {
      await adapter.insertDraft(conversation!.id, text);
      toast.success('Draft inserted into Front', {
        description: 'Your draft is ready to review and send.',
      });
      // TODO: Track feedback to API
      console.log('Draft inserted successfully');
    } catch (error) {
      console.error('Failed to insert draft:', error);
      toast.error('Failed to insert draft', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  // Register keyboard shortcuts - MUST be before any early returns!
  useKeyboardShortcuts([
    {
      key: 'mod+g',
      description: 'Generate draft',
      category: 'draft',
      handler: () => {
        if (allQuestionsAnswered && !draft && !draftMutation.isPending) {
          handleGenerateDraft();
        }
      },
    },
    {
      key: 'mod+/',
      description: 'Toggle chat interface',
      category: 'chat',
      handler: () => {
        setIsChatCollapsed((prev) => !prev);
      },
    },
    {
      key: '?',
      description: 'Show keyboard shortcuts help',
      category: 'navigation',
      handler: () => {
        openHelp();
      },
    },
  ]);

  // Early return AFTER all hooks
  if (!conversation) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-lg font-semibold">Crewflow AI</h1>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            {adapter ? (
              <>
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Waiting for conversation data from Front...
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select a conversation in Front to get started with AI-powered drafts
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <h1 className="text-lg font-semibold">Crewflow AI</h1>
        <p className="text-sm text-muted-foreground mt-1">
          AI assistant for {conversation.customer_name || conversation.customer_email}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Inactive user message */}
        {!isUserActive && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-4">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              Features Disabled
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              AI features are disabled until your account access is approved.
            </p>
          </div>
        )}
        {/* Show loading state if no messages yet */}
        {(!conversation.messages || conversation.messages.length === 0) ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-sm text-muted-foreground">
                Loading conversation messages...
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Sentiment Analysis */}
            <SentimentDisplay
              sentiment={sentiment}
              isLoading={analyzeMutation.isPending}
            />

          {/* Questions */}
          {(analyzeMutation.isSuccess || questions.length > 0) && (
            <QuestionList
              questions={questions}
              answers={answers}
              onAnswerChange={handleAnswerChange}
              isLoading={false}
            />
          )}

          {/* Generate Draft Button */}
          {analyzeMutation.isSuccess && !draft && (
            <div className="space-y-2">
              <button
                onClick={handleGenerateDraft}
                disabled={!allQuestionsAnswered || draftMutation.isPending}
                className="w-full px-4 py-3 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {draftMutation.isPending ? 'Generating...' : 'Generate Draft'}
                {!draftMutation.isPending && <KeyboardHint shortcut="mod+g" />}
              </button>

              {adapter?.fetchDraft && (
                <button
                  onClick={handleImportFromFront}
                  disabled={draftMutation.isPending}
                  className="w-full px-4 py-2 rounded-lg text-sm font-medium border bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Import from Front
                </button>
              )}
            </div>
          )}

          {/* Draft */}
          {draft && (
            <DraftEditor
              draft={draft}
              onInsert={handleInsertDraft}
              onDraftUpdate={(updatedDraft) => {
                setDraft(updatedDraft);
                setDraftInStore(updatedDraft); // Keep store in sync for feedback tracking
              }}
            />
          )}

          {/* Chat Interface (always visible if conversation exists) */}
          {conversation && crewflowConversationId && (
            <ChatInterface
              conversationId={crewflowConversationId}
              chatHistory={chatHistory}
              onDraftUpdate={(updatedDraft) => {
                // When chat updates the draft, refresh it
                if (updatedDraft) {
                  setDraft(updatedDraft);
                  setDraftInStore(updatedDraft); // Keep store in sync for feedback tracking
                }
              }}
              isCollapsed={isChatCollapsed}
              onToggleCollapse={() => setIsChatCollapsed(!isChatCollapsed)}
            />
          )}

          {/* Customer Notes */}
          {conversation && session && (
            <div className="rounded-lg border p-4">
              <CustomerNotes
                accountId={session.account_id}
                customerEmail={conversation.customer_email}
                customerName={conversation.customer_name}
                userId={session.user_id || '99999999-9999-9999-9999-999999999999'} // Fallback: demo@crewflow.com
              />
            </div>
          )}

          {/* Error Messages */}
          {analyzeMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-800">
                Failed to analyze conversation. Please try again.
              </p>
            </div>
          )}

          {draftMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-800">
                Failed to generate draft. Please try again.
              </p>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Powered by Crewflow AI
          </p>
          <button
            onClick={openHelp}
            className="text-xs text-muted-foreground hover:text-foreground underline"
            aria-label="Show keyboard shortcuts"
          >
            Keyboard shortcuts
          </button>
        </div>
      </div>

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp isOpen={isHelpOpen} onClose={closeHelp} />
    </div>
  );
}
